import { useRef, useState, useEffect } from "react";
import { Field, Formik, Form } from "formik";
import {
  Button,
  FullscreenModal,
  SelectComponent,
  Checkbox,
  PhoneWithPrefix,
  Input,
  SVG,
  Warning,
} from "shared";
import { useGetPhonePrefixes } from "hooks/phone";
import { useGetCountries } from "hooks/countries";
import * as Yup from "yup";
import { VALIDATION_ENUM, SVG_ENUM } from "enums";
import { useAddSuccessor, useAddSuccessorPayment } from "hooks/successors";
import { useGetPaymentMethods } from "hooks/payments";
import { AddPaymentMethodModal } from "screens/profilePage/components/Payments/components";
import "./AddSuccessor.scss";
import { SuccessorType } from "types/SuccessorTypes";
import { isValidPesel } from "utils/validation/peselValidation";

interface AddSuccessorProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  successor?: SuccessorType | null;
}

const AddSuccessor: React.FC<AddSuccessorProps> = ({
  isOpen,
  onClose,
  successor,
}) => {
  const [step, setStep] = useState(1);
  const [isAddPaymentMethodModalOpen, setIsAddPaymentMethodModalOpen] =
    useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successorUuid, setSuccessorUuid] = useState("");
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showCardRequiredWarning, setShowCardRequiredWarning] = useState(false);

  useEffect(() => {
    if (isOpen && successor && successor.uuid) {
      setSuccessorUuid(successor.uuid);
      setStep(2);
    } else {
      setStep(1);
    }
    setShowCardRequiredWarning(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const { mutate: addPayment } = useAddSuccessorPayment({
    onSuccess: () => {
      setIsLoading(false);
      setStep(1);
      onClose();
    },
    onError: () => {
      setIsLoading(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  });
  const { data: phonePrefixes } = useGetPhonePrefixes();
  const { data: countries } = useGetCountries();
  const { data: paymentMethods } = useGetPaymentMethods();

  const { mutate: addSuccessor } = useAddSuccessor({
    onSuccess: (data) => {
      setSuccessorUuid(data.uuid);
      setIsSubmitting(false);
      setStep(2);
    },
    onError: (data: any) => {
      if (data?.data?.isAuthorized === false) {
        onClose();
      }
      setIsSubmitting(false);
    },
  });
  const formRef = useRef<any>(null);

  type FormValues = {
    fullName: string;
    nationalId: string;
    phonePrefix: string;
    phoneNumber: string;
    email: string;
    address: string;
    postalCode: string;
    city: string;
    country: string;
    acceptance: boolean;
  };

  const initialValues: FormValues = {
    fullName: "",
    nationalId: "",
    phonePrefix: "",
    phoneNumber: "",
    email: "",
    address: "",
    postalCode: "",
    city: "",
    country: "",
    acceptance: false,
  };

  const onSubmit = async (values: FormValues) => {
    setIsSubmitting(true);
    await addSuccessor({
      phonePrefix: values.phonePrefix,
      fullName: values.fullName,
      nationalId: values.nationalId,
      phone: values.phoneNumber,
      email: values.email,
      address: values.address,
      postalCode: values.postalCode,
      city: values.city,
      country: values.country,
    });
  };

  const validation = () =>
    Yup.object().shape({
      fullName: Yup.string()
        .required(VALIDATION_ENUM.REQUIRED)
        .max(50, VALIDATION_ENUM.MAX_50)
        .min(3, VALIDATION_ENUM.MIN3)
        .matches(
          /^[^\s].*?[^\s]$/,
          VALIDATION_ENUM.CANNOT_START_AND_END_WITH_SPACE,
        ),
      nationalId: Yup.string()
        .required(VALIDATION_ENUM.REQUIRED)
        .matches(
          /^[^\s].*?[^\s]$/,
          VALIDATION_ENUM.CANNOT_START_AND_END_WITH_SPACE,
        )
        .test("pesel", VALIDATION_ENUM.PESEL, (v) => isValidPesel(v || "")),
      phonePrefix: Yup.string().required(VALIDATION_ENUM.REQUIRED),
      phoneNumber: Yup.string().required(VALIDATION_ENUM.REQUIRED),
      email: Yup.string()
        .email(VALIDATION_ENUM.WRONG_EMAIL)
        .required(VALIDATION_ENUM.REQUIRED)
        .matches(
          /^[a-zA-Z0-9_%+-]+([.][a-zA-Z0-9_%+-]+)*@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
          VALIDATION_ENUM.WRONG_EMAIL,
        ),
      address: Yup.string()
        .required(VALIDATION_ENUM.REQUIRED)
        .min(3, VALIDATION_ENUM.MIN3)
        .matches(
          /^[^\s].*?[^\s]$/,
          VALIDATION_ENUM.CANNOT_START_AND_END_WITH_SPACE,
        ),
      postalCode: Yup.string()
        .required(VALIDATION_ENUM.REQUIRED)
        .min(3, VALIDATION_ENUM.MIN3)
        .matches(
          /^[^\s].*?[^\s]$/,
          VALIDATION_ENUM.CANNOT_START_AND_END_WITH_SPACE,
        )
        .matches(/^\d{2}-\d{3}$/, VALIDATION_ENUM.POSTAL_CODE),

      city: Yup.string()
        .required(VALIDATION_ENUM.REQUIRED)
        .min(3, VALIDATION_ENUM.MIN3)
        .matches(
          /^[^\s].*?[^\s]$/,
          VALIDATION_ENUM.CANNOT_START_AND_END_WITH_SPACE,
        ),
      country: Yup.string().required(VALIDATION_ENUM.REQUIRED),
      acceptance: Yup.boolean().oneOf([true], "Pole wymagane"),
    });

  useEffect(() => {
    if (phonePrefixes && formRef.current) {
      const pl = phonePrefixes.find((prefix) => prefix.label === "+48");
      formRef.current.setFieldValue("phonePrefix", pl?.value);
    }
    if (countries && formRef.current) {
      const plCountry = countries.find(
        (country) =>
          country.label?.toLowerCase().includes("polska") ||
          country.value === "PL",
      );
      if (plCountry && formRef.current) {
        const currentCountry = formRef.current.values?.country;
        if (!currentCountry) {
          formRef.current.setFieldValue("country", plCountry.value);
        }
      }
    }
  }, [phonePrefixes, countries, formRef, isOpen]);

  useEffect(() => {
    if (paymentMethods && paymentMethods.length > 0) {
      const defaultCard = paymentMethods.find((pm) => pm.default);
      setSelectedCardId(
        defaultCard ? String(defaultCard.id) : String(paymentMethods[0].id),
      );
      setShowCardRequiredWarning(false);
    }
  }, [paymentMethods]);

  const choosePaymentMethod = (id: string) => {
    setSelectedCardId(id);
    setShowCardRequiredWarning(false);
  };

  const handleMakePayment = async (
    successorUuid: string,
    cardId: string | null,
  ) => {
    setIsLoading(true);
    await addPayment({
      payment_method_uid: cardId as string,
      successor_uuid: successorUuid,
    });
  };
  return (
    <>
      <AddPaymentMethodModal
        isOpen={isAddPaymentMethodModalOpen}
        onClose={() => setIsAddPaymentMethodModalOpen(false)}
        skipSubscriptionActivation
      />

      <FullscreenModal
        className="add-document__modal"
        isOpen={isOpen}
        onClose={onClose}
      >
        <div className="successor-auth-modal__steps add-successor__steps">
          Krok {step}/<span>2</span>
        </div>

        <h2
          className={`fullscreen-modal__header ${
            step === 2 ? "add-successor__steps-2" : ""
          }`}
        >
          {step === 1 ? "Dodaj kontakt" : "Metoda płatności"}
        </h2>

        {step === 1 && (
          <Formik
            initialValues={initialValues}
            onSubmit={(values) => onSubmit(values)}
            validationSchema={validation()}
            enableReinitialize
            innerRef={formRef}
          >
            {() => (
              <Form className="m-width-100">
                <Field
                  type="text"
                  id="fullName"
                  name="fullName"
                  maxLength={50}
                  label={"Imię i nazwisko"}
                  as={Input}
                />
                <Field
                  type="number"
                  id="nationalId"
                  name="nationalId"
                  keepZero
                  label={"Numer PESEL"}
                  maxLength={11}
                  as={Input}
                />
                <PhoneWithPrefix>
                  <Field
                    label="Telefon"
                    name="phonePrefix"
                    id="phonePrefix"
                    as={SelectComponent}
                    options={phonePrefixes}
                  />
                  <Field
                    name="phoneNumber"
                    type="number"
                    id="phoneNumber"
                    maxLength="12"
                    as={Input}
                  />
                </PhoneWithPrefix>
                <Field
                  type="text"
                  id="email"
                  name="email"
                  label={"Email"}
                  as={Input}
                  maxLength={50}
                />
                <Field
                  type="text"
                  id="address"
                  name="address"
                  label={"Adres"}
                  maxLength={200}
                  as={Input}
                />
                <Field
                  type="text"
                  id="postalCode"
                  name="postalCode"
                  maxLength={10}
                  label={"Kod pocztowy"}
                  as={Input}
                />

                <Field
                  type="text"
                  id="city"
                  maxLength={50}
                  name="city"
                  label={"Miasto"}
                  as={Input}
                />

                <Field
                  id="acceptance"
                  name="acceptance"
                  wrapperClassName="login-wrapper__acceptance checkbox--white"
                  label={
                    <p>
                      Potwierdzam, że system może wysłać korespondencję do
                      wskazanej osoby w moim imieniu
                    </p>
                  }
                  as={Checkbox}
                />

                <div className="fullscreen-modal__buttons">
                  <Button
                    label="Anuluj"
                    onClick={onClose}
                    className="button--stroke button--rounded"
                  />
                  <Button
                    onClick={() => null}
                    label={"Potwierdź"}
                    type="submit"
                    className=" button--rounded button--black"
                    isLoading={isSubmitting}
                  />
                </div>
              </Form>
            )}
          </Formik>
        )}

        {step === 2 && (
          <div className="add-successor__payment">
            <p className="add-successor__payment-copy">
              Koszt dodania sukcesora jest jednorazowy i wynosi
              <strong>
                {" "}
                199 zł za pierwszego sukcesora oraz 99 zł za każdego kolejnego.
              </strong>
            </p>

            {paymentMethods.length > 0 && (
              <p className="add-successor__payment-copy">
                Poniżej znajdują się twoje zapisane karty płatnicze. Wybierz, z
                której chcesz skorzystać lub dodaj nową.
              </p>
            )}

            <div className="payments__payment-methods--header add-successor__payment-heading">
              {paymentMethods.length > 0 && (
                <p className="payments__label">Zapisane karty</p>
              )}
              <Button
                onClick={() => setIsAddPaymentMethodModalOpen(true)}
                className="button--stroke button--rounded button--payments"
                size="small"
                label="Dodaj kartę płatniczą +"
              />
            </div>

            {paymentMethods?.map((paymentMethod) => {
              const isSelected = selectedCardId === String(paymentMethod.id);
              return (
                <div
                  key={paymentMethod.id}
                  className={`payments__payment-methods--item ${
                    isSelected ? "selected" : ""
                  }`}
                  onClick={() => choosePaymentMethod(String(paymentMethod.id))}
                >
                  <SVG
                    type={SVG_ENUM.CARD}
                    className="payments__payment-methods--card"
                  />
                  <p className="payments__payment-methods--number">
                    ****{paymentMethod.card.last4}
                  </p>
                  {isSelected && (
                    <>
                      <div className="payments__payment-methods--options">
                        Wybrano
                      </div>
                      <SVG
                        type={SVG_ENUM.CIRCLE_CHECKED_GREEN}
                        className="payments__payment-methods--checked"
                      />
                    </>
                  )}
                </div>
              );
            })}

            {showCardRequiredWarning && (
              <Warning text="Aby potwierdzić, dodaj kartę płatniczą." />
            )}

            <div className="add-successor__payment-actions">
              <Button
                label="Anuluj"
                onClick={onClose}
                size="large"
                styleType="strokeBlack"
                className="button--wide"
              />
              <Button
                isLoading={isLoading}
                label="Potwierdź"
                onClick={() => {
                  if (!selectedCardId) {
                    setShowCardRequiredWarning(true);
                    return;
                  }
                  handleMakePayment(successorUuid, selectedCardId);
                }}
                styleType="primary"
                size="large"
                className="button--wide"
              />
            </div>
          </div>
        )}
      </FullscreenModal>
    </>
  );
};

export default AddSuccessor;
